<script setup>
import { onMounted, onBeforeUnmount, ref, nextTick } from "vue";
import { usePermissionStore } from "@/stores/usePermissionStore";
import CustomTextarea from "../Generics/CustomTextarea.vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  permissionId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["close", "refresh"]);

const permissionStore = usePermissionStore();

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const obj = ref({
  permissionId: 0,
  permissionName: "",
  description: "",
  referencedTable: "",
});

const nameInput = ref(null);
const isSubmitting = ref(false);

// ====================================================================
// ✅ TOAST PADRÃO ADDUSERGROUPFORM.VUE (3000ms) 🚀
// (Copiado de AddActivityForm.vue para consistência)
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null; // Limpa a referência
  }, 3000); // 3 segundos, padrão AddUserGroupForm
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message, onFinish) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(); // Inicia o timer de 3s para o toast

  // Fecha o modal depois de 2.4s (padrão AddActivityForm.vue)
  setTimeout(() => {
    if (typeof onFinish === "function") onFinish();
  }, 2400); // Alterado de 1500ms local para 2400ms para consistência.
}
// ====================================================================

async function edit() {
  if (isSubmitting.value) return;

  // Simple validation check (can be expanded)
  if (!obj.value.permissionName || obj.value.permissionName.trim().length < 3) {
    showErrorToast("O nome da permissão deve ter pelo menos 3 caracteres.");
    return;
  }

  try {
    isSubmitting.value = true;

    await permissionStore.updatePermission(props.permissionId, obj.value);

    // ✅ Toast + fecha modal depois
    showSuccessToast("Permissão atualizada com sucesso.", () => {
      emit("refresh");
      emit("close");
      isSubmitting.value = false;
    });
  } catch (error) {
    let userFriendlyMessage =
      "Ocorreu um erro desconhecido ao salvar as alterações.";
    const errorData = error?.response?.data || error;

    if (errorData && errorData.message) {
      userFriendlyMessage = errorData.message;
    } else if (error.message && typeof error.message === "string") {
      userFriendlyMessage = error.message;
    }

    console.error("Erro detalhado da API ao salvar:", errorData);

    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

function closeModal() {
  if (!isSubmitting.value) {
    emit("close");
  }
}

function handleEscKey(event) {
  if (event.key === "Escape") {
    closeModal();
  }
}

onMounted(async () => {
  try {
    const permissionData = await permissionStore.getPermissionById(
      props.permissionId
    );
    // Clonar e popular os dados
    obj.value = { ...permissionData };
  } catch (err) {
    console.error("Erro ao carregar permissão:", err.message);
    showErrorToast("Não foi possível carregar os dados da permissão.");
  }

  nextTick(() => {
    if (nameInput.value) nameInput.value.focus();
  });

  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div class="modal-backdrop" @click.self="!isSubmitting">
    <div ref="panelRef" class="modal-panel small" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Editar Permissão</h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="edit" novalidate>
        <div class="modal-body">
          <div class="form-group">
            <label for="name" class="form-label">Nome</label>
            <input
              ref="nameInput"
              type="text"
              name="name"
              id="name"
              v-model="obj.permissionName"
              class="input-base"
              placeholder="Digite o nome da permissão"
              required
              @focus="$event.target.select()"
            />
          </div>

          <div class="form-group">
            <label for="referencedTable" class="form-label">
              Tabelas referenciadas (separe por vírgulas)
            </label>
            <input
              type="text"
              name="referencedTable"
              id="referencedTable"
              v-model="obj.referencedTable"
              class="input-base"
              placeholder="Digite o nome da tabela que terá a permissão"
              @focus="$event.target.select()"
            />
          </div>

          <div class="form-group">
            <label for="description" class="form-label">Descrição (opcional)</label>

            <CustomTextarea
              v-model:body="obj.description"
              placeholder="Digite uma breve descrição (opcional)"
              :is-disabled="false"
            />
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>

          <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>

            <svg
              v-else
              class="mr-1 -ml-1 w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
              />
            </svg>

            <span>
              {{ isSubmitting ? "Salvando..." : "Salvar Alterações" }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>


